// ============================================
// MODALCOMPARTIR.JS — Generar link público para producto/vitrina
// ============================================

import React, { useEffect, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario,
} from './helpersClientes';
import SelectorCliente, { useClienteTienda } from './SelectorCliente';
import { useUsuario } from '../context/UsuarioContext';
import { useMoneda } from './SelectorMoneda';
import { authHeaders } from '../utils/storage';
import { ligaPublica } from '../config/api';

// Construye la URL pública absoluta de un código corto
function urlPublica(tipo, codigo) {
  return ligaPublica(tipo === 'producto' ? 'p' : 'v', codigo);
}

// Modal aceptando 2 modos:
// - producto: { producto: { tipo, referencia_id, nombre, ... } }
// - vitrina:  { items: [{ tipo, referencia_id, cantidad, nombre, ... }] }
export default function ModalCompartir({ producto, items, onCerrar }) {
  const { usuario } = useUsuario();
  const [clienteCtx] = useClienteTienda();
  const { formatear } = useMoneda();

  const esVitrina = !producto && Array.isArray(items) && items.length > 0;

  const [titulo, setTitulo] = useState(producto?.nombre || `Cotización (${(items || []).length} productos)`);
  const [mensaje, setMensaje] = useState('');
  const [mostrarTel, setMostrarTel] = useState(true);
  const [mostrarWA, setMostrarWA] = useState(true);
  const [mostrarCorreo, setMostrarCorreo] = useState(true);
  const [vendedorWA, setVendedorWA] = useState('');
  const [vendedorCorreo, setVendedorCorreo] = useState(usuario?.correo || '');
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);
  const [generado, setGenerado] = useState(null); // { tipo, codigo, url, link }
  const [cliente] = useState(clienteCtx);

  useEffect(() => {
    if (usuario?.correo) setVendedorCorreo(usuario.correo);
  }, [usuario]);

  const generar = async () => {
    setError(null);
    setGenerando(true);
    try {
      const baseBody = {
        cliente_id: cliente?.id || null,
        cliente_nombre: cliente?.nombre || null,
        titulo,
        mensaje_vendedor: mensaje || null,
        mostrar_telefono: mostrarTel,
        mostrar_whatsapp: mostrarWA,
        mostrar_correo: mostrarCorreo,
        vendedor_id: usuario?.correo || 'sin-vendedor',
        vendedor_nombre: usuario?.nombre || null,
        vendedor_whatsapp: vendedorWA || null,
        vendedor_correo: vendedorCorreo || null,
      };
      let r;
      if (esVitrina) {
        r = await fetch(`${API_BASE_CLI}/api/compartir/vitrina`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...baseBody,
            productos: items.map(it => ({
              tipo: it.tipo,
              referencia_id: it.referencia_id,
              cantidad: it.cantidad || 1,
              notas: it.notas || null,
            })),
          }),
        }).then(x => x.json());
      } else {
        r = await fetch(`${API_BASE_CLI}/api/compartir/producto`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...baseBody,
            producto_tipo: producto.tipo,
            producto_referencia_id: producto.referencia_id,
          }),
        }).then(x => x.json());
      }
      if (!r.ok) throw new Error(r.error || 'no se pudo generar');
      const tipo = r.link?.tipo || (esVitrina ? 'vitrina' : 'producto');
      setGenerado({
        tipo,
        codigo: r.codigo,
        url: urlPublica(tipo, r.codigo),
        link: r.link,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerando(false);
    }
  };

  const copiar = async (texto) => {
    try { await navigator.clipboard.writeText(texto); } catch {}
  };

  const enviarWhatsApp = () => {
    if (!generado) return;
    const msg = encodeURIComponent(
      `Hola${cliente?.nombre ? ` ${cliente.nombre.split(' ')[0]}` : ''}, te comparto ${esVitrina ? 'esta cotización' : 'este producto'}: ${generado.url}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const correoMailto = () => {
    if (!generado) return;
    const subject = encodeURIComponent(titulo || 'Cotización Beep');
    const body = encodeURIComponent(
      `${mensaje ? mensaje + '\n\n' : ''}Te comparto ${esVitrina ? 'esta cotización' : 'este producto'}:\n\n${generado.url}\n\n— ${usuario?.nombre || 'Beep México'}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const totalVitrina = esVitrina
    ? (items || []).reduce((s, it) => s + (Number(it.precio_unitario_mxn || it.precio_efectivo_mxn || 0) * Number(it.cantidad || 1)), 0)
    : null;

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(10,20,40,0.55)', zIndex: 1400,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
          fontFamily: PP, color: COLOR_GRIS, maxHeight: '92vh', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{
          padding: '16px 24px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>
            🔗 {esVitrina ? 'Compartir vitrina' : 'Compartir producto'}
          </div>
          <button
            onClick={onCerrar}
            style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: COLOR_GRIS_CLARO }}
          >×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {!generado ? (
            <>
              {/* Resumen producto/vitrina */}
              <div style={{
                background: COLOR_FONDO, borderRadius: 8, padding: 12, marginBottom: 14,
                fontSize: 13, color: COLOR_GRIS,
              }}>
                {esVitrina ? (
                  <>
                    <div style={{ fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>
                      {(items || []).length} producto(s)
                    </div>
                    {(items || []).slice(0, 5).map((it, i) => (
                      <div key={i} style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>
                        × {Number(it.cantidad || 1)} — {it.nombre}
                      </div>
                    ))}
                    {(items || []).length > 5 && (
                      <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>
                        … y {items.length - 5} más
                      </div>
                    )}
                    {totalVitrina != null && (
                      <div style={{ marginTop: 6, fontWeight: 700, color: COLOR_OSCURO }}>
                        Total: {formatear(totalVitrina, 'MXN')}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 700, color: COLOR_OSCURO }}>{producto.nombre}</div>
                    <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                      Precio: {formatear(producto.precio_efectivo_mxn || 0, 'MXN')}
                    </div>
                  </>
                )}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Cliente (opcional)</div>
                <SelectorCliente />
                <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                  Si seleccionas cliente con lista pactada, los precios pactados se aplican.
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Título de la página pública</div>
                <input
                  style={estiloInput} value={titulo}
                  onChange={(e) => setTitulo(e.target.value.slice(0, 200))}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Mensaje opcional (max 200 caracteres)</div>
                <textarea
                  style={{ ...estiloInput, minHeight: 60, resize: 'vertical' }}
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value.slice(0, 200))}
                  placeholder="Hola Juan, te recomiendo este equipo…"
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Datos de contacto a mostrar</div>
                <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
                  <label style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={mostrarTel} onChange={(e) => setMostrarTel(e.target.checked)} />
                    {' '}Mi teléfono
                  </label>
                  <label style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={mostrarWA} onChange={(e) => setMostrarWA(e.target.checked)} />
                    {' '}Mi WhatsApp
                  </label>
                  <label style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={mostrarCorreo} onChange={(e) => setMostrarCorreo(e.target.checked)} />
                    {' '}Mi correo
                  </label>
                </div>
              </div>

              {(mostrarWA || mostrarCorreo) && (
                <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                  {mostrarWA && (
                    <div>
                      <div style={lbl}>Tu WhatsApp (con código país)</div>
                      <input
                        style={estiloInput} value={vendedorWA}
                        onChange={(e) => setVendedorWA(e.target.value)}
                        placeholder="525512345678"
                      />
                    </div>
                  )}
                  {mostrarCorreo && (
                    <div>
                      <div style={lbl}>Tu correo</div>
                      <input
                        style={estiloInput} value={vendedorCorreo}
                        onChange={(e) => setVendedorCorreo(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div style={{ marginBottom: 12, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
                  ⚠️ {error}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{
                padding: 20, borderRadius: 12, background: '#e8faf5',
                border: `2px solid #16a34a`, marginBottom: 16, textAlign: 'center',
              }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}>¡Link generado!</div>
              </div>

              <div style={{
                padding: 12, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, marginBottom: 12,
                background: COLOR_FONDO, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all',
              }}>
                {generado.url}
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <button
                  style={{ ...estiloBotonSecundario, flex: 1, minWidth: 120 }}
                  onClick={() => copiar(generado.url)}
                >📋 Copiar link</button>
                <button
                  style={{ ...estiloBotonPrimario, flex: 1, minWidth: 120, background: '#25D366' }}
                  onClick={enviarWhatsApp}
                >💬 WhatsApp</button>
                <button
                  style={{ ...estiloBotonSecundario, flex: 1, minWidth: 120 }}
                  onClick={correoMailto}
                >✉️ Correo</button>
              </div>

              <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, textAlign: 'center' }}>
                El link es público y no requiere login. Cada vista queda registrada en "Mis links".
              </div>
            </>
          )}
        </div>

        <div style={{
          padding: '14px 24px', borderTop: `1px solid ${COLOR_BORDE}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>
            {generado ? 'Cerrar' : 'Cancelar'}
          </button>
          {!generado && (
            <button
              style={{ ...estiloBotonPrimario, opacity: generando ? 0.6 : 1 }}
              onClick={generar} disabled={generando}
            >
              {generando ? 'Generando…' : '🔗 Generar link'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 11, fontWeight: 600, color: COLOR_GRIS, marginBottom: 4 };
